import { createContext, useCallback, useContext, useEffect, useState } from 'react';

const LangContext = createContext(null);

// Word joiner: an invisible, zero-width character that forbids a line break at
// its position. Inserted between the characters of a CJK word so the word never
// wraps mid-character (e.g. 私人/跨境), while breaks stay allowed *between* words
// and at punctuation/spaces.
const WJ = '⁠';
const _cjkCache = new Map();
let _segmenter;

// Terms the segmentation dictionary tends to mis-split (跨- compounds, the firm
// name) but that must never wrap mid-word. These are force-bound after the
// dictionary pass. Add new terms here and they're protected site-wide.
const NOBREAK_TERMS = ['跨法域', '跨境', '跨代', '德铉', '私人', '财富'];

// Bind a term's characters together with word joiners wherever it appears.
function bindTerm(text, term) {
  if (term.length < 2 || !text.includes(term)) return text;
  return text.split(term).join([...term].join(WJ));
}

// Keep Chinese words intact across line wraps. Two passes:
//   1. Guarantee — force-bind curated terms the dictionary mis-splits (跨境,
//      德铉 …). Done first so partial dictionary binding can't block a match.
//   2. Broad — the browser dictionary (Intl.Segmenter: Chrome 87+, Safari 14.1+,
//      Firefox 125+) binds the remaining recognised words (海外, 顾问 …); any
//      segment already carrying a joiner is left as-is.
// Where Intl.Segmenter is unavailable, only pass 1 runs (curated terms still
// protected). Results are memoised since t() runs on every render.
function cjkWordWrap(text) {
  if (!text) return text;
  const cached = _cjkCache.get(text);
  if (cached !== undefined) return cached;
  let out = text;
  for (const term of NOBREAK_TERMS) out = bindTerm(out, term);
  if (typeof Intl !== 'undefined' && Intl.Segmenter) {
    if (!_segmenter) _segmenter = new Intl.Segmenter('zh', { granularity: 'word' });
    let acc = '';
    for (const { segment, isWordLike } of _segmenter.segment(out)) {
      acc += (isWordLike && segment.length > 1 && !segment.includes(WJ) && /[㐀-鿿]/.test(segment))
        ? [...segment].join(WJ)
        : segment;
    }
    out = acc;
  }
  _cjkCache.set(text, out);
  return out;
}

// Pure picker — works outside of React too (e.g. in plain JS callers).
// For CN, words are kept intact across line wraps (see cjkWordWrap).
export function t(entry, lang) {
  if (entry == null) return '';
  const str = typeof entry === 'string' ? entry : (entry[lang] ?? entry.en ?? '');
  return lang === 'cn' ? cjkWordWrap(str) : str;
}

export function LangProvider({ children, initial = 'en' }) {
  const [lang, setLangState] = useState(() => {
    try {
      const saved = localStorage.getItem('lang');
      if (saved === 'en' || saved === 'cn') return saved;
    } catch (_) {}
    return initial;
  });

  // Keep <html lang> in sync on first mount (incl. when the language is restored
  // from localStorage without a click) and on every change. Without this the
  // attribute stays at index.html's default ("en") while CN is displayed,
  // breaking screen readers, SEO, and any [lang]-scoped CSS.
  useEffect(() => {
    try { document.documentElement.lang = lang === 'cn' ? 'zh-CN' : 'en'; } catch (_) {}
  }, [lang]);

  const setLang = useCallback((l) => {
    setLangState(l);
    try { localStorage.setItem('lang', l); } catch (_) {}
  }, []);

  return (
    <LangContext.Provider value={{ lang, setLang }}>
      {children}
    </LangContext.Provider>
  );
}

export function useLang() {
  const ctx = useContext(LangContext);
  if (!ctx) throw new Error('useLang must be used within <LangProvider>');
  return ctx;
}

// Convenience hook: returns a t-function already bound to current lang.
export function useT() {
  const { lang } = useLang();
  return useCallback((entry) => t(entry, lang), [lang]);
}
